// CRM Clients — operator roster (new UI, replaces the classic ClientsList).
// Full parity: account + plan filters, show-inactive, search, create/edit modal
// with every field and the track->tier cascade, status lifecycle, delete.
// Row + "open" route to the new CRM workspace, never the old UI.
// Super-admin / team-member only.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import pricing from '../../data/pricing';

const SECTORS = [
  'Technology, Information and Media', 'Financial Services', 'Hospitals and Health Care',
  'Manufacturing', 'Professional Services', 'Consumer Services', 'Retail', 'Education',
  'Real Estate and Equipment Rental', 'Construction', 'Transportation, Logistics, Supply Chain and Storage',
  'Energy, Mining and Utilities', 'Government Administration', 'Non-profit Organizations',
  'Entertainment Providers', 'Accommodation and Food Services', 'Administrative and Support Services',
  'Farming, Ranching and Forestry', 'Civic and Social Organizations', 'International Affairs',
  'Public Safety', 'Holding Companies', 'Religious Institutions', 'Other'
];
const TRACK_OPTIONS = Object.entries(pricing).map(([key, val]) => ({ key, label: val.label }));
function tierOptions(track) {
  if (!track || !pricing[track]) return [];
  return Object.entries(pricing[track].tiers).map(([key, val]) => ({ key, ...val }));
}
function tierLabel(track, tier) {
  return pricing[track]?.tiers?.[tier]?.label || tier || '';
}
const EMPTY_FORM = { name: '', sector: '', website: '', contact_name: '', contact_email: '', service_track: '', service_tier: '', notes: '', account_id: '', account_name: '', cta_url: '', landing_page_url: '', attachment_url: '', attachment_name: '' };

function Favicon({ website, name, size = 40 }) {
  const [failed, setFailed] = useState(false);
  let domain = '';
  try { domain = website ? new URL(website.startsWith('http') ? website : 'https://' + website).hostname : ''; } catch (_) {}
  const initials = (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  if (!domain || failed) return <div className="crmcl-fav crmcl-fav--fb" style={{ width: size, height: size }}>{initials}</div>;
  return <div className="crmcl-fav" style={{ width: size, height: size }}><img alt="" src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} onError={() => setFailed(true)} /></div>;
}

export default function CrmClients() {
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filterAccount, setFilterAccount] = useState('all');
  const [filterTrack, setFilterTrack] = useState('all');
  const [showInactive, setShowInactive] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [accountMode, setAccountMode] = useState('new');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/clients`);
      if (r.ok) setClients(await r.json());
    } catch (_) {} finally { setLoading(false); }
  }, []);
  const loadAccounts = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/accounts`, { headers: { Authorization: 'Bearer ' + token } });
      if (r.ok) { const d = await r.json(); setAccounts(Array.isArray(d) ? d : (d.accounts || [])); }
    } catch (_) {}
  }, [getToken]);
  useEffect(() => { load(); loadAccounts(); }, [load, loadAccounts]);

  function openCreate() { setEditing(null); setForm(EMPTY_FORM); setAccountMode('new'); setShowForm(true); }
  function openEdit(e, c) {
    e.stopPropagation();
    setEditing(c);
    setForm({
      name: c.name || '', sector: c.sector || '', website: c.website || '',
      contact_name: c.contact_name || '', contact_email: c.contact_email || '',
      service_track: c.service_track || '', service_tier: c.service_tier || '',
      notes: c.notes || '', account_id: c.account_id || '',
      cta_url: c.cta_url || '', landing_page_url: c.landing_page_url || '',
      attachment_url: c.attachment_url || '', attachment_name: c.attachment_name || '', account_name: ''
    });
    setAccountMode('existing');
    setShowForm(true);
  }

  const selectedAccount = accounts.find(a => String(a.id) === String(form.account_id));
  const capNote = (() => {
    if (!selectedAccount) return null;
    const used = selectedAccount.workspace_count ?? 0;
    const plural = used === 1 ? '' : 's';
    if (selectedAccount.plan_track === 'internal') return { ok: true, text: 'Internal \u00b7 no workspace limit' };
    const td = pricing[selectedAccount.plan_track]?.tiers?.[selectedAccount.plan_tier];
    if (!td) return { ok: false, text: used + ' workspace' + plural + ' \u00b7 no plan tier set' };
    return { ok: true, text: td.name + ' \u00b7 ' + used + ' workspace' + plural };
  })();

  async function save() {
    if (!form.name.trim()) { toast.error('Client name is required'); return; }
    const creatingAccount = !editing && accountMode === 'new';
    if (!editing && !creatingAccount && !form.account_id) { toast.error('Pick an account, or create a new one.'); return; }
    if (creatingAccount && !form.service_track) { toast.error('Pick a plan track for the new account.'); return; }
    setSaving(true);
    try {
      const url = editing ? `${API_BASE}/clients/${editing.id}` : (creatingAccount ? `${API_BASE}/accounts/with-workspace` : `${API_BASE}/clients`);
      const method = editing ? 'PUT' : 'POST';
      const token = await getToken();
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(creatingAccount ? { ...form, account_name: (form.account_name || form.name).trim(), plan_track: form.service_track, plan_tier: form.service_tier || null } : form) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d.error || 'Could not save client'); setSaving(false); return; }
      toast.success(editing ? 'Client updated' : (creatingAccount ? 'Account and first workspace created' : 'Client created'));
      setShowForm(false); load();
    } catch (e) { toast.error('Could not save client'); }
    setSaving(false);
  }

  async function setStatus(e, c, status) {
    e.stopPropagation();
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/clients/${c.id}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ status }) });
      if (!r.ok) { toast.error('Could not update status'); return; }
      toast.success(status === 'active' ? 'Reactivated' : 'Deactivated');
      load();
    } catch (_) { toast.error('Could not update status'); }
  }

  async function del(e, c) {
    e.stopPropagation();
    if (!window.confirm(`Delete ${c.name}? This removes the client and its data. This cannot be undone.`)) return;
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/clients/${c.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { toast.error('Could not delete'); return; }
      toast.success('Client deleted');
      load();
    } catch (_) { toast.error('Could not delete'); }
  }

  const filtered = clients.filter(c => {
    const s = c.status || 'active';
    if (!showInactive && s !== 'active') return false;
    if (filterAccount !== 'all' && String(c.account_id) !== String(filterAccount)) return false;
    if (filterTrack !== 'all' && c.account_plan_track !== filterTrack) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      const hay = [c.name, c.sector, c.website, c.contact_name, c.contact_email, c.account_name].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const activeCount = clients.filter(c => (c.status || 'active') === 'active').length;

  return (
    <div className="crmcl">
      <div className="crmcl-head">
        <div>
          <h1 className="crmcl-title">Clients</h1>
          <div className="crmcl-sub">{activeCount} active {activeCount === 1 ? 'client' : 'clients'}</div>
        </div>
        <button className="crmcl-new" onClick={openCreate}><span className="crmcl-new-ic">+</span>New client</button>
      </div>

      <div className="crmcl-toolbar">
        <div className="crmcl-search">
          <svg viewBox="0 0 16 16" width="14" height="14"><circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input placeholder="Search clients" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <select className="crmcl-select" value={filterAccount} onChange={e => setFilterAccount(e.target.value)}>
          <option value="all">All accounts</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select className="crmcl-select" value={filterTrack} onChange={e => setFilterTrack(e.target.value)}>
          <option value="all">All plans</option>
          {TRACK_OPTIONS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <label className="crmcl-toggle">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
      </div>

      {loading ? <p className="crmcl-empty">Loading clients...</p>
        : filtered.length === 0 ? <p className="crmcl-empty">No clients match.</p>
        : (
          <div className="crmcl-list">
            {filtered.map(c => {
              const s = c.status || 'active';
              return (
                <div key={c.id} className="crmcl-card" onClick={() => navigate(`/clients/${c.id}/crm/home`)}>
                  <Favicon website={c.website} name={c.name} />
                  <div className="crmcl-main">
                    <div className="crmcl-name">{c.name}</div>
                    <div className="crmcl-meta">{c.sector || 'No sector'}{c.website ? ' · ' + c.website.replace(/^https?:\/\//, '') : ''}</div>
                    {(c.contact_name || c.contact_email) && (
                      <div className="crmcl-contact">{c.contact_name}{c.contact_name && c.contact_email ? ' · ' : ''}{c.contact_email}</div>
                    )}
                  </div>
                  <div className="crmcl-tags">
                    {c.account_name && <span className="crmcl-chip crmcl-chip--acct">{c.account_name}</span>}
                    {c.account_plan_track && pricing[c.account_plan_track] && <span className="crmcl-chip crmcl-chip--plan">{tierLabel(c.account_plan_track, c.account_plan_tier) || pricing[c.account_plan_track]?.label}</span>}
                    <span className={'crmcl-status crmcl-status--' + s}>{s}</span>
                  </div>
                  <div className="crmcl-actions" onClick={e => e.stopPropagation()}>
                    <button className="crmcl-act" onClick={e => { e.stopPropagation(); navigate(`/crm/clients/${c.id}/profile`); }}>Profile</button>
                    <button className="crmcl-act" onClick={e => openEdit(e, c)}>Edit</button>
                    {s === 'active'
                      ? <button className="crmcl-act crmcl-act--warn" onClick={e => setStatus(e, c, 'inactive')}>Deactivate</button>
                      : <button className="crmcl-act crmcl-act--ok" onClick={e => setStatus(e, c, 'active')}>Reactivate</button>}
                    <button className="crmcl-act crmcl-act--danger" onClick={e => del(e, c)}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {showForm && (
        <div className="crmcl-overlay" onClick={() => !saving && setShowForm(false)}>
          <div className="crmcl-modal" onClick={e => e.stopPropagation()}>
            <div className="crmcl-modal-head">
              <h2>{editing ? 'Edit client' : 'New client'}</h2>
              <button className="crmcl-x" onClick={() => setShowForm(false)}>×</button>
            </div>

            <div className="crmcl-grid">
              <div className="crmcl-field crmcl-col2">
                <label>Client name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme Ltd" />
              </div>
              <div className="crmcl-field">
                <label>Sector</label>
                <select value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })}>
                  <option value="">Select sector</option>
                  {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="crmcl-field">
                <label>Website</label>
                <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="acme.co.uk" />
              </div>
              <div className="crmcl-field">
                <label>Contact name</label>
                <input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Jane Smith" />
              </div>
              <div className="crmcl-field">
                <label>Contact email</label>
                <input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="jane@acme.co.uk" />
              </div>
              <div className="crmcl-accpanel crmcl-col2">
                <div className="crmcl-acclab">Account</div>
                {!editing && (
                  <div className="crmcl-accseg">
                    <button type="button" className={accountMode === 'new' ? 'is-on' : ''} onClick={() => setAccountMode('new')}>Create a new account</button>
                    <button type="button" className={accountMode === 'existing' ? 'is-on' : ''} onClick={() => setAccountMode('existing')}>Add to an existing one</button>
                  </div>
                )}
                {(!editing && accountMode === 'new') ? (
                  <div className="crmcl-grid">
                    <div className="crmcl-field crmcl-col2">
                      <label>Account name</label>
                      <input value={form.account_name} onChange={e => setForm({ ...form, account_name: e.target.value })} placeholder={form.name || 'Defaults to the client name'} />
                      <div className="crmcl-acchint">Leave blank to use the client name. This is the billing entity.</div>
                    </div>
                    <div className="crmcl-field">
                      <label>Plan track *</label>
                      <select value={form.service_track} onChange={e => setForm({ ...form, service_track: e.target.value, service_tier: '' })}>
                        <option value="">Select track</option>
                        {TRACK_OPTIONS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="crmcl-field">
                      <label>Plan tier</label>
                      <select value={form.service_tier} onChange={e => setForm({ ...form, service_tier: e.target.value })} disabled={!form.service_track}>
                        <option value="">{form.service_track ? 'Select tier' : 'Pick a track first'}</option>
                        {tierOptions(form.service_track).map(t => <option key={t.key} value={t.key}>{t.name} — {t.label}</option>)}
                      </select>
                    </div>
                    <div className="crmcl-col2"><span className="crmcl-accchip">Creates the account and this first workspace together</span></div>
                  </div>
                ) : (
                  <div className="crmcl-grid">
                    <div className="crmcl-field crmcl-col2">
                      <select value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })}>
                        <option value="">Select an account</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                      <div className="crmcl-acchint">Plan and workspace limit come from this account.</div>
                    </div>
                    {capNote && <div className="crmcl-col2"><span className={capNote.ok ? 'crmcl-accchip crmcl-accchip--ok' : 'crmcl-accchip'}>{capNote.text}</span></div>}
                  </div>
                )}
              </div>
              <div className="crmcl-field crmcl-col2">
                <label>Notes</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes" />
              </div>

              <div className="crmcl-field crmcl-col2"><div className="crmcl-divider">Links (optional)</div></div>
              <div className="crmcl-field">
                <label>CTA URL</label>
                <input value={form.cta_url} onChange={e => setForm({ ...form, cta_url: e.target.value })} placeholder="Booking or CTA link" />
              </div>
              <div className="crmcl-field">
                <label>Landing page URL</label>
                <input value={form.landing_page_url} onChange={e => setForm({ ...form, landing_page_url: e.target.value })} placeholder="Landing page" />
              </div>
              <div className="crmcl-field">
                <label>Attachment URL</label>
                <input value={form.attachment_url} onChange={e => setForm({ ...form, attachment_url: e.target.value })} placeholder="File link" />
              </div>
              <div className="crmcl-field">
                <label>Attachment name</label>
                <input value={form.attachment_name} onChange={e => setForm({ ...form, attachment_name: e.target.value })} placeholder="Shown label" />
              </div>
            </div>

            <div className="crmcl-modal-actions">
              <button className="crmcl-btn crmcl-btn--ghost" onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
              <button className="crmcl-btn crmcl-btn--primary" onClick={save} disabled={saving || !form.name.trim()}>{saving ? 'Saving...' : (editing ? 'Save changes' : 'Create client')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
